import qs, { type IStringifyOptions } from "qs";

type QueryPrimitive = string | number | boolean | null | undefined;
type QueryValue =
  | QueryPrimitive
  | QueryPrimitive[]
  | Record<string, unknown>
  | Array<Record<string, unknown> | QueryPrimitive>;

export type QueryParams = Record<string, QueryValue>;

const imageModules = import.meta.glob<string>("../assets/images/**/*.{png,jpg,jpeg,svg,webp,gif,avif}", {
  eager: true,
  import: "default",
});

function normalizeFolder(folder: string): string {
  let start = 0;
  let end = folder.length;

  while (start < end && folder[start] === "/") {
    start += 1;
  }

  while (end > start && folder[end - 1] === "/") {
    end -= 1;
  }

  return folder.slice(start, end);
}

function normalizeImageName(image: string): string {
  let start = 0;

  while (start < image.length && image[start] === "/") {
    start += 1;
  }

  return image.slice(start);
}

function buildImageKey(image: string, folder = "") {
  const normalizedFolder = normalizeFolder(folder);
  const normalizedImage = normalizeImageName(image);

  return normalizedFolder
    ? `../assets/images/${normalizedFolder}/${normalizedImage}`
    : `../assets/images/${normalizedImage}`;
}

function removeNullishDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== null && item !== undefined)
      .map((item) => removeNullishDeep(item)) as T;
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== null && item !== undefined)
        .map(([key, item]) => [key, removeNullishDeep(item)])
    ) as T;
  }

  return value;
}

/**
 * Resolve uma imagem local do diretório assets/images de forma compatível com Vite.
 * Retorna fallback caso o asset não exista.
 */
export function loadLocalImage(
  image: string,
  folder = "",
  fallback = ""
): string {
  const key = buildImageKey(image, folder);
  return imageModules[key] ?? fallback;
}

/**
 * Lê um parâmetro da query string atual ou de uma query string informada.
 * Ex.: "?utm_source=google"
 */
export function getQueryUrlParam(
  param: string,
  search: string = typeof window !== "undefined" ? window.location.search : ""
): string | null {
  if (!param) {
    return null;
  }

  const normalizedSearch = search.startsWith("?") ? search : `?${search}`;
  const queryParams = new URLSearchParams(normalizedSearch);

  return queryParams.get(param);
}

/**
 * Adiciona ou sobrescreve query params em uma URL preservando:
 * - query string existente
 * - hash (#...)
 *
 * Mantém suporte a objetos aninhados e arrays por usar qs.
 */
export function setQueryParams(
  url: string,
  params: QueryParams,
  options?: IStringifyOptions
): string {
  const [urlWithoutHash, hash = ""] = url.split("#");
  const [basePath, currentSearch = ""] = urlWithoutHash.split("?");

  const currentParams = qs.parse(currentSearch, {
    ignoreQueryPrefix: true,
  });

  const sanitizedParams = removeNullishDeep(params);

  const mergedParams = {
    ...currentParams,
    ...sanitizedParams,
  };

  const queryString = qs.stringify(mergedParams, {
    skipNulls: true,
    encodeValuesOnly: true,
    arrayFormat: "repeat",
    ...options,
  });

  const finalQuery = queryString ? `?${queryString}` : "";
  const finalHash = hash ? `#${hash}` : "";

  return `${basePath}${finalQuery}${finalHash}`;
}